import {
  DEFAULT_PAGE_SIZE,
  EXTERNAL_TEAMS_ACCESS_SORT_BY,
  EXTERNAL_TEAMS_ACCESS_TABLE_COLUMNS,
} from '@common/constants';
import { OrderBy } from '@common/types';
import { OverlayPage, TableWrapper } from '@components/partials';
import { FilterData } from '@components/partials/TableWrapper/parts/Filter';
import { PeopleSortBy } from '@hooks/people/types';
import { useIsDesktop } from '@hooks/utils';
import { usePagination } from '@hooks/utils/pagination';

import { formatDate, useTableData } from '@utils/index';
import React, { useState } from 'react';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import styles from './styles.module.css';

import { useExternalTeamsAccessPreviewQuery } from '@hooks/security';
import { useNavSourceRedirect } from '@hooks/utils/dashboard';
import { ExternalTeamsAccessSnapshot } from '@pages/Dashboard/tiles';
import { TeamsWithExternalAccess } from '@pages/Dashboard/tiles/TeamsWithExternalAccess';
import { createCellValue } from './utils';
import { RecommendationCard } from '@pages/Dashboard/tiles/helpers/RecommendationCard';
import useDownloadCsv from '@hooks/utils/export';
import { API_ENDPOINTS } from '@api/ApiEndpoints';

const pageSize = DEFAULT_PAGE_SIZE;

const ExternalTeamsAccessPage: React.FC = () => {
  const isDesktop = useIsDesktop();
  const [search, setSearch] = useState('');
  const { pageNumber, setPageNumber } = usePagination();
  const [queryParams, setQueryParams] = useQueryParams({
    search: withDefault(StringParam, ''),
    sortBy: withDefault(StringParam, 'email'),
    order: withDefault(StringParam, 'asc'),
    filter: withDefault(StringParam, ''),
  });

  const redirectPath = useNavSourceRedirect('/insights-and-analytics/assurance');
  const FILTERS = ['over30', 'over60', 'over90'];
  const transformedFilterData: FilterData[] = [];

  transformedFilterData.push({
    label: 'Last accessed',
    name: 'collaboration',
    options: [
      { label: 'Over 30 days ago', value: 'over30' },
      { label: 'Over 60 days ago', value: 'over60' },
      {
        label: 'Over 90 days ago',
        value: 'over90',
      },
    ],
  });

  // table
  const query = useExternalTeamsAccessPreviewQuery({
    pageSize,
    pageNumber,
    searchTerm: search,
    sortBy: queryParams.sortBy as PeopleSortBy,
    order: queryParams.order as OrderBy,
    externalTeamsCollaboration: queryParams.filter
      ? queryParams.filter
          .split(',')
          .filter((filter) => FILTERS.includes(filter))
      : [],
  });
  const getTableData = useTableData(
    query,
    EXTERNAL_TEAMS_ACCESS_TABLE_COLUMNS,
    createCellValue
  );

  const [shouldDownload, setShouldDownload] = useState(false);
  const { refetch: downloadCsv } = useDownloadCsv({
    shouldDownload,
    endpoint: `${API_ENDPOINTS.EXTERNAL_TEAMS_ACCESS_PREVIEW}/export`,
    params: {
      pageSize,
      pageNumber,
      searchTerm: search,
      sortBy: queryParams.sortBy as PeopleSortBy,
      order: queryParams.order as OrderBy,
      externalTeamsCollaboration: queryParams.filter
        ? queryParams.filter
            .split(',')
            .filter((filter) => FILTERS.includes(filter))
        : [],
    },
    filename: `Clevr360_External_Teams_access_${formatDate(
      new Date().toString()
    )}.csv`,
  });

  const handleDownloadClick = () => {
    setShouldDownload(true);
    downloadCsv();
    setShouldDownload(false);
  };

  return (
    <OverlayPage
      isFooterVisible={false}
      contentClassNames={''}
      path={redirectPath}
      headerTitle={`External Teams access`}
    >
      <>
        <div className={styles.pageGrid}>
          {isDesktop && (
            <>
              <ExternalTeamsAccessSnapshot
                params=""
                classNames="col-span-3 xl:col-auto"
              />

              <TeamsWithExternalAccess
                params=""
                classNames="col-span-3 xl:col-auto"
              />

              <RecommendationCard
                insightDescription={'external Teams access'}
              />
            </>
          )}
        </div>
        <div className="mt-2">
          <TableWrapper
            testId='external-teams-access-table'
            search={search}
            setSearch={setSearch}
            searchPlaceholder={'Search by email address'}
            columns={EXTERNAL_TEAMS_ACCESS_TABLE_COLUMNS}
            data={getTableData()}
            sortData={EXTERNAL_TEAMS_ACCESS_SORT_BY}
            filterData={transformedFilterData}
            searchKey="search"
            query={query}
            refetchQuery={query.refetch}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            setPageNumber={setPageNumber}
            isCollapsable={true}
            sliceColumns={2}
            floatingFilterButton={true}
            hasFilterBanner={false}

            searchCountStatsLabel="external users with access to Teams"
            filterHeaderText="Filter external Teams access"
            isDownloadAvailable={true}
            handleDownload={handleDownloadClick}
          />
        </div>
      </>
    </OverlayPage>
  );
};

export default ExternalTeamsAccessPage;
